import { useCallback, useMemo, useReducer, useRef } from 'react'

import { CreateDirectUploadDocument, CreateDirectUploadMutation } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'

import { calculateChecksum } from 'modules/core/hooks/useDirectUpload/calculateChecksum'

import {
  FileUpload,
  FileStatus,
  MembershipWithSignedIds,
  ParticipantFiles,
  ParticipantFilesMap,
  ParticipantInfo,
} from './types'

type DirectUploadsHeader = NonNullable<
  NonNullable<
    CreateDirectUploadMutation['directUploadsMutations']['createDirectuploadsDirectUpload']
  >['directUpload']
>['headers'][number]

// Action type constants
const ActionTypes = {
  INIT: 'INIT',
  ADD_FILE: 'ADD_FILE',
  UPDATE_FILE_PROGRESS: 'UPDATE_FILE_PROGRESS',
  FILE_SUCCESS: 'FILE_SUCCESS',
  FILE_ERROR: 'FILE_ERROR',
  REMOVE_FILE: 'REMOVE_FILE',
  RESET: 'RESET',
} as const

type Action =
  | { type: typeof ActionTypes.INIT; payload: ParticipantInfo[] }
  | { type: typeof ActionTypes.ADD_FILE; payload: { membershipId: number; fileUpload: FileUpload } }
  | {
      type: typeof ActionTypes.UPDATE_FILE_PROGRESS
      payload: { membershipId: number; fileId: string; progress: number }
    }
  | {
      type: typeof ActionTypes.FILE_SUCCESS
      payload: { membershipId: number; fileId: string; signedId: string }
    }
  | {
      type: typeof ActionTypes.FILE_ERROR
      payload: { membershipId: number; fileId: string; error: string }
    }
  | { type: typeof ActionTypes.REMOVE_FILE; payload: { membershipId: number; fileId: string } }
  | { type: typeof ActionTypes.RESET }

function reducer(state: ParticipantFilesMap, action: Action): ParticipantFilesMap {
  switch (action.type) {
    case ActionTypes.INIT: {
      const newMap = new Map<number, ParticipantFiles>()
      action.payload.forEach((participant) => {
        newMap.set(participant.membershipId, {
          membershipId: participant.membershipId,
          employeeId: participant.employeeId,
          employeeName: participant.employeeName,
          files: [],
        })
      })
      return newMap
    }

    case ActionTypes.ADD_FILE: {
      const newMap = new Map(state)
      const participant = newMap.get(action.payload.membershipId)
      if (participant) {
        newMap.set(action.payload.membershipId, {
          ...participant,
          files: [...participant.files, action.payload.fileUpload],
        })
      }
      return newMap
    }

    case ActionTypes.UPDATE_FILE_PROGRESS: {
      const newMap = new Map(state)
      const participant = newMap.get(action.payload.membershipId)
      if (participant) {
        newMap.set(action.payload.membershipId, {
          ...participant,
          files: participant.files.map((f) =>
            f.id === action.payload.fileId ? { ...f, progress: action.payload.progress } : f
          ),
        })
      }
      return newMap
    }

    case ActionTypes.FILE_SUCCESS: {
      const newMap = new Map(state)
      const participant = newMap.get(action.payload.membershipId)
      if (participant) {
        newMap.set(action.payload.membershipId, {
          ...participant,
          files: participant.files.map((f) =>
            f.id === action.payload.fileId
              ? { ...f, status: FileStatus.SUCCESS, signedId: action.payload.signedId, progress: 1 }
              : f
          ),
        })
      }
      return newMap
    }

    case ActionTypes.FILE_ERROR: {
      const newMap = new Map(state)
      const participant = newMap.get(action.payload.membershipId)
      if (participant) {
        newMap.set(action.payload.membershipId, {
          ...participant,
          files: participant.files.map((f) =>
            f.id === action.payload.fileId
              ? { ...f, status: FileStatus.ERROR, error: action.payload.error }
              : f
          ),
        })
      }
      return newMap
    }

    case ActionTypes.REMOVE_FILE: {
      const newMap = new Map(state)
      const participant = newMap.get(action.payload.membershipId)
      if (participant) {
        newMap.set(action.payload.membershipId, {
          ...participant,
          files: participant.files.filter((f) => f.id !== action.payload.fileId),
        })
      }
      return newMap
    }

    case ActionTypes.RESET: {
      return new Map()
    }

    default:
      return state
  }
}

interface UseDirectUploadPerParticipantOptions {
  trainingId: number
}

export const useDirectUploadPerParticipant = ({
  trainingId,
}: UseDirectUploadPerParticipantOptions) => {
  const { company } = useCurrentSession()
  const [participantFilesMap, dispatch] = useReducer(reducer, new Map())
  const xhrRefs = useRef<Map<string, XMLHttpRequest>>(new Map())

  const [createDirectUpload] = useMutation(CreateDirectUploadDocument, {
    extractorFn: (data: CreateDirectUploadMutation) =>
      data.directUploadsMutations.createDirectuploadsDirectUpload,
  })

  const initializeParticipants = useCallback((participants: ParticipantInfo[]) => {
    dispatch({ type: ActionTypes.INIT, payload: participants })
  }, [])

  const uploadFile = useCallback(
    async (membershipId: number, file: File) => {
      const fileId = `${membershipId}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`

      const fileUpload: FileUpload = {
        id: fileId,
        file,
        signedId: null,
        progress: 0,
        status: FileStatus.UPLOADING,
      }

      dispatch({ type: ActionTypes.ADD_FILE, payload: { membershipId, fileUpload } })

      try {
        // Calculate checksum
        const checksum = await calculateChecksum(file)

        // Create direct upload request
        const handled = handleResult(
          await createDirectUpload({
            variables: {
              byteSize: file.size,
              checksum,
              contentType: file.type,
              filename: file.name,
              companyId: company.id,
              resourceType: 'Trainings::Training',
              resourceId: String(trainingId),
            },
          }),
          (errors) => {
            throw new Error(errors?.message || 'Failed to create direct upload')
          }
        )

        if (!handled.success || !handled.data.directUpload) {
          throw new Error('Failed to create direct upload: No data returned')
        }

        const { url, signedId, headers } = handled.data.directUpload

        // Upload to object storage
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          xhrRefs.current.set(fileId, xhr)

          xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable) {
              const progress = event.loaded / event.total
              dispatch({
                type: ActionTypes.UPDATE_FILE_PROGRESS,
                payload: { membershipId, fileId, progress },
              })
            }
          })

          xhr.addEventListener('load', () => {
            xhrRefs.current.delete(fileId)
            if (xhr.status >= 200 && xhr.status < 300) {
              dispatch({
                type: ActionTypes.FILE_SUCCESS,
                payload: { membershipId, fileId, signedId },
              })
              resolve()
            } else {
              reject(new Error(`Upload failed with status ${xhr.status}`))
            }
          })

          xhr.addEventListener('error', () => {
            xhrRefs.current.delete(fileId)
            reject(new Error('Upload failed due to network error'))
          })

          xhr.addEventListener('abort', () => {
            xhrRefs.current.delete(fileId)
            dispatch({
              type: ActionTypes.FILE_ERROR,
              payload: { membershipId, fileId, error: 'Upload cancelled' },
            })
            resolve()
          })

          xhr.open('PUT', url)
          xhr.setRequestHeader('Content-Type', file.type)
          xhr.setRequestHeader('Content-MD5', checksum)

          const alreadySetHeaders = new Set(['content-type', 'content-md5'])
          headers
            .filter(
              (header: DirectUploadsHeader) => !alreadySetHeaders.has(header.name.toLowerCase())
            )
            .forEach((header: DirectUploadsHeader) =>
              xhr.setRequestHeader(header.name, header.value)
            )

          xhr.send(file)
        })
      } catch (error) {
        dispatch({
          type: ActionTypes.FILE_ERROR,
          payload: {
            membershipId,
            fileId,
            error: error instanceof Error ? error.message : 'Upload failed',
          },
        })
      }
    },
    [company.id, createDirectUpload, trainingId]
  )

  const uploadFiles = useCallback(
    (membershipId: number, files: File[]) => {
      files.forEach((file) => {
        uploadFile(membershipId, file)
      })
    },
    [uploadFile]
  )

  const removeFile = useCallback((membershipId: number, fileId: string) => {
    // Cancel ongoing upload if exists
    const xhr = xhrRefs.current.get(fileId)
    if (xhr) {
      xhr.abort()
      xhrRefs.current.delete(fileId)
    }

    dispatch({ type: ActionTypes.REMOVE_FILE, payload: { membershipId, fileId } })
  }, [])

  const reset = useCallback(() => {
    // Cancel all ongoing uploads
    xhrRefs.current.forEach((xhr) => xhr.abort())
    xhrRefs.current.clear()
    dispatch({ type: ActionTypes.RESET })
  }, [])

  const hasUploadsInProgress = useMemo(() => {
    for (const participant of participantFilesMap.values()) {
      if (
        participant.files.some(
          (f) => f.status === FileStatus.UPLOADING || f.status === FileStatus.PENDING
        )
      ) {
        return true
      }
    }
    return false
  }, [participantFilesMap])

  const hasAnyFiles = useMemo(() => {
    for (const participant of participantFilesMap.values()) {
      if (participant.files.some((f) => f.status === FileStatus.SUCCESS)) {
        return true
      }
    }
    return false
  }, [participantFilesMap])

  const hasErrors = useMemo(() => {
    for (const participant of participantFilesMap.values()) {
      if (participant.files.some((f) => f.status === FileStatus.ERROR)) {
        return true
      }
    }
    return false
  }, [participantFilesMap])

  const getMembershipsWithSignedIds = useCallback((): MembershipWithSignedIds[] => {
    const result: MembershipWithSignedIds[] = []

    participantFilesMap.forEach((participant) => {
      const signedIds = participant.files
        .filter(
          (f): f is FileUpload & { signedId: string } =>
            f.status === FileStatus.SUCCESS && f.signedId !== null
        )
        .map((f) => f.signedId)

      if (signedIds.length > 0) {
        result.push({
          membershipId: participant.membershipId,
          signedIds,
        })
      }
    })

    return result
  }, [participantFilesMap])

  return {
    participantFilesMap,
    initializeParticipants,
    uploadFile,
    uploadFiles,
    removeFile,
    reset,
    hasUploadsInProgress,
    hasAnyFiles,
    hasErrors,
    getMembershipsWithSignedIds,
  }
}
